/* eslint-disable react/jsx-curly-brace-presence */
import React, { useLayoutEffect, useRef, useState } from 'react'
import { useStaticQuery, graphql } from 'gatsby'

// Elements
import Loading from 'components/shared/Loading'
import ParseContent from 'components/shared/ParseContent'

// Duuf
import {
  FormDuuf,
  FormDuufProps,
  FormDuufGroup,
  FormDuufSubmit,
  useFormDuuf,
} from '@ubo/form-duuf'

export { FormDuufGroup, FormDuufSubmit, useFormDuuf }

export const MAX_UPLOAD_FILE_BYTES = 10 * 1024 * 1024
// PHP post_max_size is 30M; stay a bit under that for multipart overhead.
export const PHP_SAFE_UPLOAD_BYTES = 28 * 1024 * 1024

const SUBMIT_ERROR_MESSAGE =
  'Er is iets fout gegaan. Controleer de gegevens en of de bestanden niet groter zijn dan 10MB.'

const FILE_TOO_LARGE_SERVER_MESSAGE =
  'Het verzenden is mislukt omdat het bestand te groot is voor de server. Gebruik een kleiner bestand (max. 10MB per stuk) of verklein de afbeelding.'

function didServerDropPost(
  messages: Record<string, string>,
  values: Record<string, unknown>
) {
  const filledButReportedRequired = Object.entries(messages).filter(
    ([id, message]) => {
      if (!String(message).toLowerCase().includes('vereist')) {
        return false
      }

      const value = values[`input_${id}`]

      if (typeof value === 'string') {
        return value.trim().length > 0
      }

      return Boolean(value)
    }
  )

  return filledButReportedRequired.length >= 2
}

function applyGravityFormsMessages(
  formik: {
    setFieldError: Function
    setFieldTouched: Function
  },
  messages: Record<string, string>
) {
  Object.entries(messages).forEach(([id, message]) => {
    const fieldId = `input_${id}`
    formik.setFieldError(fieldId, message)
    formik.setFieldTouched(fieldId, true, false)
  })
}

interface FormProps
  extends Omit<
    FormDuufProps,
    'graphqlData' | 'renderStatus' | 'renderLoading'
  > {
  className?: string
  children?: React.ReactNode
}

function stripEmptyUploadFields(
  values: Record<string, unknown>,
  formData: FormData
) {
  Object.keys(values).forEach((key) => {
    const value = values[key]
    const emptyFile =
      typeof File !== 'undefined' &&
      value instanceof File &&
      (value.size === 0 || !value.name)

    if (value === '' || value == null || emptyFile) {
      formData.delete(key)
    }
  })
}

const Form: React.FC<FormProps> = ({
  className = '',
  children,
  onSubmit,
  ...rest
}) => {
  const { allGfForm } = useStaticQuery<GatsbyTypes.formDuufQueryQuery>(graphql`
    query formDuufQuery {
      allGfForm {
        edges {
          node {
            formId
            slug
            apiURL
            formFields {
              id
              label
              labelPlacement
              type
              isRequired
              visibility
              placeholder
              checkboxLabel
              choices
              description
            }
            button {
              text
            }
            confirmations {
              message
            }
          }
        }
      }
    }
  `)

  return (
    <FormDuuf
      className={className}
      encType="multipart/form-data"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      graphqlData={allGfForm}
      onSubmit={(values, formData) => {
        stripEmptyUploadFields(values, formData)
        onSubmit?.(values, formData)
      }}
      renderLoading={() => <Loading />}
      renderStatus={(status) =>
        status?.status === 'error' ? (
          <ParseContent content={SUBMIT_ERROR_MESSAGE} />
        ) : (
          <ParseContent content={status?.content || ''} />
        )
      }
    >
      <>
        <PreserveFormOnError />
        {children}
      </>
    </FormDuuf>
  )
}

function PreserveFormOnError() {
  const { formik } = useFormDuuf()
  const [error, setError] = useState<string | null>(null)
  const errorRef = useRef<HTMLDivElement>(null)
  const keepSubmittingFalseRef = useRef(false)
  const formikRef = useRef(formik)
  const pendingMessagesRef = useRef<Record<string, string> | null>(null)

  formikRef.current = formik

  useLayoutEffect(() => {
    const originalFetch = window.fetch.bind(window)

    window.fetch = async (...args: Parameters<typeof window.fetch>) => {
      const response = await originalFetch(...args)
      const request = args[0]
      let url = ''

      if (typeof request === 'string') {
        url = request
      } else if (request instanceof Request) {
        url = request.url
      } else {
        url = String(request)
      }

      if (url.includes('/gf/v2/forms/') && url.includes('/submissions')) {
        try {
          const payload = await response.clone().json()
          if (payload?.is_valid === false && payload.validation_messages) {
            pendingMessagesRef.current = payload.validation_messages
          }
        } catch {
          // Ignore non-JSON responses
        }
      }

      return response
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [])

  useLayoutEffect(() => {
    const originalSetStatus = formik.setStatus.bind(formik)
    const originalSetSubmitting = formik.setSubmitting.bind(formik)

    formik.setStatus = (status: string) => {
      if (status === 'error') {
        keepSubmittingFalseRef.current = true
        const messages = pendingMessagesRef.current
        pendingMessagesRef.current = null

        if (messages) {
          const droppedPost = didServerDropPost(
            messages,
            formikRef.current.values
          )

          if (!droppedPost) {
            applyGravityFormsMessages(formikRef.current, messages)
          }

          setError(
            droppedPost
              ? FILE_TOO_LARGE_SERVER_MESSAGE
              : SUBMIT_ERROR_MESSAGE
          )
        } else {
          setError(SUBMIT_ERROR_MESSAGE)
        }

        originalSetSubmitting(false)
        return
      }

      keepSubmittingFalseRef.current = false
      setError(null)
      originalSetStatus(status)
    }

    formik.setSubmitting = (value: boolean) => {
      if (keepSubmittingFalseRef.current && value) {
        keepSubmittingFalseRef.current = false
        originalSetSubmitting(false)
        return
      }

      originalSetSubmitting(value)
    }

    return () => {
      formik.setStatus = originalSetStatus
      formik.setSubmitting = originalSetSubmitting
    }
  }, [formik])

  useLayoutEffect(() => {
    if (!error || !errorRef.current) {
      return
    }

    errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [error])

  if (!error) {
    return null
  }

  return (
    <div
      ref={errorRef}
      className="form-duuf-something-wrong mb-4"
      role="alert"
    >
      {error}
    </div>
  )
}

export default Form
